import React, { useState } from 'react';
import { ArrowLeft, Car, DollarSign, Clock, Truck, Gavel, FileCheck, Package, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { useUserRequests, RequestStatus } from '../hooks/useUserRequests';
import { motion, AnimatePresence } from 'framer-motion';
import { Footer } from '../components/Footer';
import { Navbar } from '../components/Navbar';
import { WhatsAppBubble } from '../components/WhatsAppBubble';
interface MyCarsPageProps {
  onBack: () => void;
  onNavigate: (page: string) => void;
}
const statusSteps: {
  id: RequestStatus;
  label: string;
  icon: React.ElementType;
}[] = [{
  id: 'received',
  label: 'Recibido',
  icon: FileCheck
}, {
  id: 'searching',
  label: 'Búsqueda',
  icon: Search
}, {
  id: 'auction',
  label: 'Subasta',
  icon: Gavel
}, {
  id: 'purchased',
  label: 'Comprado',
  icon: DollarSign
}, {
  id: 'shipping',
  label: 'Tránsito',
  icon: Truck
}, {
  id: 'customs',
  label: 'Aduana',
  icon: Package
}, {
  id: 'delivered',
  label: 'Entregado',
  icon: Car
}];
export function MyCarsPage({
  onBack,
  onNavigate
}: MyCarsPageProps) {
  const {
    requests
  } = useUserRequests();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const getStatusIndex = (status: RequestStatus) => {
    return statusSteps.findIndex(s => s.id === status);
  };
  const toggleExpand = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-857">
      <Navbar onNavigate={onNavigate} data-id="element-858" />
      <div className="flex-1 pt-20 pb-12 px-4 sm:px-6 lg:px-8" data-id="element-859">
        <div className="max-w-4xl mx-auto" data-id="element-860">
          <div className="flex items-center mb-8" data-id="element-861">
            <button onClick={onBack} className="flex items-center text-gray-600 hover:text-blue-600 transition-colors" data-id="element-862">
              <ArrowLeft className="w-5 h-5 mr-2" data-id="element-863" />
              Volver al inicio
            </button>
          </div>

          <div className="mb-8" data-id="element-864">
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-865">
              Mis Solicitudes
            </h1>
            <p className="text-gray-500 mt-2" data-id="element-866">
              {requests.length > 0 ? `Tienes ${requests.length} solicitud${requests.length > 1 ? 'es' : ''} activa${requests.length > 1 ? 's' : ''}.` : 'Da seguimiento al estado de tus importaciones y cotizaciones.'}
            </p>
          </div>

          {requests.length === 0 ? <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center" data-id="element-867">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-868">
                <Car className="w-10 h-10 text-gray-400" data-id="element-869" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-870">
                No tienes solicitudes activas
              </h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto" data-id="element-871">
                Aún no has solicitado ninguna cotización. ¡Encuentra tu carro
                ideal y comienza el proceso hoy mismo!
              </p>
              <button onClick={() => onNavigate('car-finder')} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg transition-all hover:-translate-y-0.5" data-id="element-872">
                Buscar mi Carro Ideal
              </button>
            </div> : <div className="space-y-6" data-id="element-873">
              {requests.map((request, index) => {
            const currentStepIndex = getStatusIndex(request.status);
            const isExpanded = expandedId === request.id;
            const imageKey = `mycar-${request.id}`;
            const imageFailed = failedImages.has(imageKey);
            return <motion.div key={request.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.1
            }} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-874">
                    {/* Header */}
                    <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50 border-b border-gray-100" data-id="element-875">
                      <div className="flex items-center gap-4" data-id="element-876">
                        <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-white flex-shrink-0" data-id="element-877">
                          {imageFailed ? <div className="w-full h-full flex items-center justify-center bg-gray-100" data-id="element-878">
                              <Car className="w-8 h-8 text-gray-300" data-id="element-879" />
                            </div> : <img src={request.car.image} alt={`${request.car.year} ${request.car.make} ${request.car.model}`} className="w-full h-full object-cover" onError={() => setFailedImages(prev => {
                      const next = new Set(prev);
                      next.add(imageKey);
                      return next;
                    })} data-id="element-880" />}
                        </div>
                        <div data-id="element-881">
                          <h3 className="text-lg font-bold text-gray-900" data-id="element-882">
                            {request.car.year} {request.car.make}{' '}
                            {request.car.model}
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-883">
                            {request.car.trim}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 sm:flex-col sm:items-end" data-id="element-884">
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-885">
                          {request.statusLabel}
                        </div>
                        <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-886">
                          <Clock className="w-3 h-3" data-id="element-887" />
                          {new Date(request.date).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                        </p>
                      </div>
                    </div>

                    {/* Price + expand toggle */}
                    <div className="p-5 sm:p-6" data-id="element-888">
                      <div className="flex items-center justify-between mb-4" data-id="element-889">
                        <div className="flex items-start gap-3" data-id="element-890">
                          <div className="p-2 bg-green-50 rounded-lg" data-id="element-891">
                            <DollarSign className="w-5 h-5 text-green-600" data-id="element-892" />
                          </div>
                          <div data-id="element-893">
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide" data-id="element-894">
                              Presupuesto Estimado
                            </p>
                            <p className="font-bold text-green-600" data-id="element-895">
                              ${request.priceRange.min.toLocaleString()} - $
                              {request.priceRange.max.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <button onClick={() => toggleExpand(request.id)} className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors px-3 py-1.5 rounded-lg hover:bg-blue-50" data-id="element-896">
                          {isExpanded ? 'Ocultar' : 'Ver progreso'}
                          {isExpanded ? <ChevronUp className="w-4 h-4" data-id="element-897" /> : <ChevronDown className="w-4 h-4" data-id="element-898" />}
                        </button>
                      </div>

                      {/* Compact progress bar (always visible) */}
                      <div className="relative" data-id="element-899">
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-900">
                          <motion.div className="h-full bg-blue-600 rounded-full" initial={{
                      width: 0
                    }} animate={{
                      width: `${currentStepIndex / (statusSteps.length - 1) * 100}%`
                    }} transition={{
                      duration: 0.8,
                      ease: 'easeOut'
                    }} data-id="element-901" />
                        </div>
                        <div className="flex justify-between mt-1.5" data-id="element-902">
                          <span className="text-[10px] text-gray-400" data-id="element-903">
                            Recibido
                          </span>
                          <span className="text-[10px] text-gray-400" data-id="element-904">
                            Entregado
                          </span>
                        </div>
                      </div>

                      {/* Expanded timeline */}
                      <AnimatePresence data-id="element-905">
                        {isExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} className="overflow-hidden" data-id="element-906">
                            <div className="mt-6 pt-6 border-t border-gray-100" data-id="element-907">
                              <div className="relative flex justify-between w-full" data-id="element-908">
                                {/* Background line */}
                                <div className="absolute top-5 left-0 w-full h-0.5 bg-gray-200" data-id="element-909"></div>
                                <div className="absolute top-5 left-0 h-0.5 bg-blue-600 transition-all duration-500" style={{
                          width: `${currentStepIndex / (statusSteps.length - 1) * 100}%`
                        }} data-id="element-910"></div>

                                {statusSteps.map((step, idx) => {
                          const isCompleted = idx < currentStepIndex;
                          const isCurrent = idx === currentStepIndex;
                          const isFuture = idx > currentStepIndex;
                          const Icon = step.icon;
                          return <div key={step.id} className="flex flex-col items-center z-10" style={{
                            width: `${100 / statusSteps.length}%`
                          }} data-id="element-911">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 bg-white transition-all duration-300
                                          ${isCompleted ? 'border-blue-600 text-blue-600 bg-blue-50' : ''}
                                          ${isCurrent ? 'border-blue-600 text-blue-600 ring-4 ring-blue-100 shadow-md scale-110' : ''}
                                          ${isFuture ? 'border-gray-200 text-gray-300' : ''}
                                        `} data-id="element-912">
                                        <Icon className="w-4 h-4" data-id="element-913" />
                                      </div>
                                      <span className={`text-[10px] sm:text-xs mt-2 font-medium text-center leading-tight
                                          ${isCompleted || isCurrent ? 'text-blue-900' : 'text-gray-400'}
                                          ${isCurrent ? 'font-bold' : ''}
                                        `} data-id="element-914">
                                        {step.label}
                                      </span>
                                    </div>;
                        })}
                              </div>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </motion.div>;
          })}

              {/* Add another car button */}
              <div className="text-center pt-4" data-id="element-915">
                <button onClick={() => onNavigate('car-finder')} className="text-blue-600 hover:text-blue-800 font-medium text-sm hover:underline" data-id="element-916">
                  + Buscar otro vehículo
                </button>
              </div>
            </div>}
        </div>
      </div>
      <Footer data-id="element-917" />
      <WhatsAppBubble data-id="element-918" />
    </div>;
}